import React from 'react';
import type { QuestionnaireRevisionContext } from '@gcv/shared';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HistoryIcon from '@mui/icons-material/History';
import { Box } from '@mui/material';

import { Header, Text } from 'ui/atoms';
import palette from 'ui/theme/palette';

interface Props {
  revision: QuestionnaireRevisionContext;
  dueDate: string;
  requestedByName?: string;
  completed?: boolean;
}

export const RevisionRequestBanner: React.FC<Props> = ({
  revision,
  dueDate,
  requestedByName,
  completed = false
}) => {
  const hasRequester = !!requestedByName && requestedByName.trim().length > 0;

  const heading = completed ? 'Revision Submitted' : 'Revision Requested';
  const Icon = completed ? HistoryIcon : ErrorOutlineIcon;
  const containerSx = completed
    ? {
        background: palette.background.secondary,
        borderColor: palette.border,
        color: palette.text.secondary
      }
    : {
        background: palette.error.background,
        borderColor: palette.error.border
      };
  const iconColor = completed ? palette.text.secondary : palette.error.dark;
  const noteBackground = completed ? palette.background.info : palette.background.error;

  return (
    <Box
      data-cy="revision-request-banner"
      sx={{
        ...containerSx,
        borderWidth: '2px',
        borderStyle: 'solid',
        p: 2,
        mb: 2,
        borderRadius: '4px',
        width: '100%'
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
        <Icon sx={{ color: iconColor, mt: '2px' }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
          <Header type="h3" content={heading} />
          {(hasRequester || !completed) && (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
              {hasRequester && (
                <Box data-testid="revision-request-requester" data-cy="revision-request-requester">
                  <Text type="body2" content={`Requested by ${requestedByName}`} />
                </Box>
              )}
              {!completed && (
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
                  <CalendarMonthIcon sx={{ fontSize: 16, color: palette.text.secondary }} />
                  <Text type="body2" content={`Due ${dueDate}`} />
                </Box>
              )}
            </Box>
          )}
          {revision.note && (
            <Box
              data-cy="revision-request-note"
              data-testid="revision-request-note"
              sx={{
                background: noteBackground,
                borderRadius: '4px',
                p: 1.5
              }}
            >
              <Text type="body2" content={revision.note} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
